<script setup lang="ts">
import type {
  CmsElementCrossSelling,
  SliderElementConfig,
} from "@shopware/composables";
import { computed, ref, useTemplateRef } from "vue";
import { useCmsElementConfig } from "#imports";

const props = defineProps<{
  content: CmsElementCrossSelling;
}>();

const { getConfigValue } = useCmsElementConfig(props.content);
const currentTabIndex = ref<number>(0);
const { setCrossSellings } = useProductSetConfiguration();

const crossSellCollections = computed(() => {
  return (
    props.content?.data?.crossSellings?.filter(
      (collection) =>
        !!collection.products.length &&
        collection.crossSelling?.display !== "pluginOnly"
    ) || []
  );
});

const toggleTab = (index: number) => {
  if (currentTabIndex.value === index) return;
  currentTabIndex.value = index;
};

const crossSellSlider = ref(null);

const crossSellSwiperSlider = useSwiper(crossSellSlider, {
  effect: "slide",
  loop: true,
  grabCursor: true,
  slidesPerView: 1,
  breakpoints: {
    540: {
      slidesPerView: 2,
    },
    768: {
      slidesPerView: 3,
    },
    1024: {
      slidesPerView: 4,
    },
    1200: {
      slidesPerView: 5,
    },
    1520: {
      slidesPerView: 6,
    },
  },
});

const isSliderReady = ref(false);
onMounted(() => {
  if (props.content?.data?.crossSellings?.length) {
    setCrossSellings(props.content?.data?.crossSellings);
  }
  isSliderReady.value = true;
});
</script>

<template>
  <div
    ref="crossSellContainer"
    class="cms-element-cross-selling pb-20 md:pb-28 lg:pb-40 overflow-hidden relative"
  >
    <template v-if="isSliderReady">
      <div class="flex gap-10 mb-5 container mx-auto">
        <h3
          v-for="(collection, index) of crossSellCollections"
          :key="index"
          class="transition cursor-pointer h3_head"
          :class="{
            'border-b-3 border-primary text-primary': currentTabIndex === index,
          }"
          @click="toggleTab(index)"
        >
          {{ collection.crossSelling.name }}
        </h3>
      </div>
      <div
        v-if="crossSellCollections.length"
        @click="crossSellSwiperSlider.prev()"
        class="swiper-cust-button-prev bg-cust_cream text-cust_gray03"
      >
        <i class="fa-solid fa-arrow-left"></i>
      </div>
      <swiper-container
        ref="crossSellSlider"
        v-if="crossSellCollections.length"
      >
        <swiper-slide
          v-for="product of crossSellCollections[currentTabIndex].products"
        >
          <ProductCard
            :product="product"
            :layout-type="getConfigValue('boxLayout')"
            :display-mode="getConfigValue('displayMode')"
          />
        </swiper-slide>
      </swiper-container>
      <div
        v-if="crossSellCollections.length"
        @click="crossSellSwiperSlider.next()"
        class="swiper-cust-button-next bg-cust_cream text-cust_gray03"
      >
        <i class="fa-solid fa-arrow-right"></i>
      </div>
    </template>
    <div v-else class="flex">
      <div
        v-for="index in 4"
        :key="index"
        data-testid="loading"
        class="w-1/2 lg:w-1/3 xl:w-1/4 px-2"
      >
        <ProductCardSkeleton class="w-full mb-8 px-1 sm:mr-8 mb-8" />
      </div>
    </div>
  </div>
</template>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
